import React from "react"
import {
  UseFormRegister,
  FieldErrors,
  Path,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/24/solid"

import { useDropdownPosition } from "hooks/utils/use-dropdown-position"
import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question } from "types"

interface DropdownQuestionProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
}

const DropdownQuestion: React.FC<DropdownQuestionProps> = ({
  question,
  register,
  errors,
  watch,
  setValue,
  ticketId,
}) => {
  const { t } = useTranslation()
  const sortedChoices = question.choices?.sort((a, b) => a.rank - b.rank) || []
  const { buttonRef, getDropdownClassName } = useDropdownPosition({
    itemCount: sortedChoices.length + 1, // +1 for "Select one..." option
    mobileThreshold: 0.5,
    spaceBuffer: 100,
  })

  const questionLabel = getLocalizedText(question.text, "Question")

  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const fieldError = errors.tickets?.[ticketId]?.[question.uuid]

  const fieldValue = watch(fieldName)
  const selectedChoiceId =
    Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0] : null
  const selectedChoice = selectedChoiceId
    ? sortedChoices.find((choice) => choice.id === selectedChoiceId)
    : null

  return (
    <div>
      <label
        htmlFor={`dropdown-${question.uuid}`}
        className="block mb-3 text-sm font-medium text-theme_text"
      >
        {questionLabel}
        {question.requires_answer && (
          <span
            className="ml-1 text-red-500"
            aria-label={t("validation.required", "Required")}
          >
            *
          </span>
        )}
      </label>

      <Listbox
        value={selectedChoiceId}
        onChange={(choiceId: number | null) => {
          const value = choiceId ? [choiceId] : []
          setValue(fieldName, value as any, { shouldValidate: true })
        }}
      >
        <div className="relative">
          <ListboxButton
            id={`dropdown-${question.uuid}`}
            ref={buttonRef}
            className="relative py-2 pr-10 pl-3 w-full text-sm text-left bg-white rounded-md border cursor-pointer focus:border-theme_accent focus:outline-none focus:ring-1 focus:ring-theme_accent border-input_border"
            aria-describedby={fieldError ? `${question.uuid}-error` : undefined}
            aria-invalid={fieldError ? "true" : "false"}
            aria-required={question.requires_answer ? "true" : "false"}
          >
            {({ open }) => (
              <>
                <span
                  className={`block truncate ${
                    selectedChoice ? "text-theme_text" : "text-gray-400"
                  }`}
                >
                  {selectedChoice
                    ? getLocalizedText(selectedChoice.text, "Choice")
                    : t("dropdown.select_one", "Select one")}
                </span>
                <span className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
                  <ChevronDownIcon
                    className={`h-5 w-5 transition-transform duration-200 text-theme_text_medium ${
                      open ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </span>
              </>
            )}
          </ListboxButton>

          <ListboxOptions
            className={({ open }) => getDropdownClassName(open)}
            modal={false}
            aria-modal={false}
          >
            <ListboxOption
              value={null}
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                  active
                    ? "bg-theme_accent/10 text-theme_accent"
                    : "text-theme_text"
                }`
              }
            >
              <span className="block text-gray-400 truncate">
                {t("dropdown.select_one", "Select one")}
              </span>
            </ListboxOption>

            {sortedChoices.map((choice) => (
              <ListboxOption
                key={choice.id}
                value={choice.id}
                className={({ active, selected }) =>
                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    active
                      ? "bg-theme_accent/10 text-theme_accent"
                      : "text-theme_text"
                  } ${selected ? "font-medium" : "font-normal"}`
                }
              >
                <span className="block truncate">
                  {getLocalizedText(choice.text, "Choice")}
                </span>

                {selectedChoiceId === choice.id && (
                  <span className="flex absolute inset-y-0 right-0 items-center pr-3 text-theme_accent">
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      <input
        type="hidden"
        {...register(fieldName, {
          required: question.requires_answer
            ? t("validation.option_required", "Please select an option")
            : false,
          validate: (value) => {
            if (!question.requires_answer && (!value || value === "")) {
              return true
            }
            if (question.requires_answer && (!value || value === "")) {
              return t("validation.option_required", "Please select an option")
            }
            return true
          },
        })}
      />

      {fieldError && (
        <p
          className="mt-1 text-sm text-red-500"
          role="alert"
          id={`${question.uuid}-error`}
        >
          {fieldError.message}
        </p>
      )}
    </div>
  )
}

export { DropdownQuestion }
